/**
 * TimePickerDropdown — Dropdown with 30-minute increments from 6:00 AM to 11:30 PM.
 *
 * We avoid <input type="time"> because mobile browsers render it inconsistently
 * (some show a wheel picker, some show a 12-hour text input, some have poor
 * touch targets). A plain <select> is uniform, accessible, and friendly to
 * 44px-height touch targets.
 *
 * Values are stored and emitted in 24-hour "HH:MM" format. Labels follow the
 * locale's clock — "6:30 PM" for en-US, "18:30" elsewhere (finding 22).
 */
import React, { useMemo } from 'react';
import { formatClock, uses12HourClock } from '../../lib/time';

export interface TimePickerDropdownProps {
  value: string; // "HH:MM"
  onChange: (value: string) => void;
  id?: string;
  ariaLabel?: string;
  className?: string;
  /** First selectable hour (inclusive, 24-hour). Default 6. */
  startHour?: number;
  /** Last selectable hour (inclusive, 24-hour). Default 23. */
  endHour?: number;
  /** 12-hour labels with AM/PM. Defaults from the browser locale (en-US only). */
  twelveHour?: boolean;
}

interface TimeOption {
  value: string; // "HH:MM"
  label: string; // "6:00 AM" or "06:00"
}

function buildOptions(startHour: number, endHour: number, twelveHour: boolean): TimeOption[] {
  const out: TimeOption[] = [];
  for (let h = startHour; h <= endHour; h++) {
    for (const m of [0, 30]) {
      // Don't go past endHour:30
      if (h === endHour && m === 30 && endHour === 23) {
        // 11:30 PM is fine; include it.
      }
      const hh = String(h).padStart(2, '0');
      const mm = String(m).padStart(2, '0');
      const value = `${hh}:${mm}`;
      const label = formatClock(h, m, twelveHour);

      out.push({ value, label });
    }
  }
  return out;
}

export const TimePickerDropdown: React.FC<TimePickerDropdownProps> = ({
  value,
  onChange,
  id,
  ariaLabel,
  className = '',
  startHour = 6,
  endHour = 23,
  twelveHour = uses12HourClock(),
}) => {
  const options = useMemo(
    () => buildOptions(startHour, endHour, twelveHour),
    [startHour, endHour, twelveHour],
  );

  // If the current value isn't in our 30-minute grid (e.g. legacy "20:15"),
  // include it as an extra option so it's preserved until the user picks something new.
  const hasExactMatch = options.some((opt) => opt.value === value);
  const allOptions = hasExactMatch
    ? options
    : [{ value, label: formatLabel(value, twelveHour) }, ...options];

  return (
    <select
      id={id}
      aria-label={ariaLabel}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={
        className ||
        'w-full px-3 py-2.5 text-base rounded-lg border-2 border-gray-300 bg-white focus:outline-none focus:border-sage-500 focus:ring-2 focus:ring-sage-500 min-h-[44px] transition-colors'
      }
    >
      {allOptions.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
};

function formatLabel(hhmm: string, twelveHour: boolean): string {
  const [h, m] = hhmm.split(':').map((n) => parseInt(n, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return hhmm;
  return formatClock(h, m, twelveHour);
}

export default TimePickerDropdown;
